import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';
import { Spinner } from './Spinner';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'nav' | 'download';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  loading?: boolean;
  active?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', className, loading, disabled, active, children, ...rest },
  ref,
) {
  let variantClass = '';
  switch (variant) {
    case 'primary':
      variantClass = cn(
        'inline-flex items-center justify-center gap-2 px-4 py-2 rounded-[6px]',
        'bg-[#0410BD] text-white font-semibold text-sm',
        'hover:bg-[#3F4CFF] active:bg-[#12122C]',
        'focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-[#3F4CFF]/30',
        'disabled:bg-[#BABACE] disabled:text-white disabled:cursor-not-allowed',
        'transition-colors duration-100',
      );
      break;
    case 'nav':
      variantClass = cn(
        'inline-flex items-center gap-2 px-3 py-2 text-sm font-semibold text-white/80',
        'hover:bg-white/10 hover:text-white transition-colors',
        active && 'bg-white/15 text-white',
      );
      break;
    case 'download':
      variantClass = cn(
        'inline-flex items-center justify-center gap-2 px-3 py-2 rounded-[6px]',
        'bg-[#0410BD] text-white font-semibold text-sm',
        'hover:bg-[#3F4CFF] transition-colors duration-100',
      );
      break;
    case 'ghost':
      variantClass = cn(
        'inline-flex items-center gap-2 px-3 py-2 rounded-[6px]',
        'text-[#12122C] dark:text-[#C4C4DC] font-medium text-sm',
        'hover:bg-[#EFF0FF] dark:hover:bg-[#2A2A4A] transition-colors duration-120',
      );
      break;
    case 'danger':
      variantClass = cn(
        'inline-flex items-center gap-2 px-3 py-2 rounded-[6px]',
        'bg-[#DC2626] text-white font-semibold text-sm',
        'hover:bg-[#B91C1C] transition-colors',
        'disabled:opacity-60',
      );
      break;
    case 'secondary':
    default:
      variantClass = cn(
        'inline-flex items-center gap-2 px-3 py-2 rounded-[6px]',
        'bg-white dark:bg-[#1A1A2E] text-[#0410BD] dark:text-[#818CF8] border border-[#BABACE] dark:border-[#3A3A5A] font-semibold text-sm',
        'hover:bg-[#F2F2F8] dark:hover:bg-[#2A2A4A] hover:border-[#BABACE] transition-colors duration-100',
        'disabled:opacity-60',
      );
      break;
  }

  return (
    <button
      ref={ref}
      className={cn(variantClass, className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <Spinner size={14} /> : null}
      <span className={cn(loading && 'opacity-80')}>{children}</span>
    </button>
  );
});
